import * as React from 'react';
import {
  Button,
  Input,
  makeStyles,
  tokens,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { Marker, MarkerCreate } from '../../../types/marker';
import { ApiErrorInfo } from '../../../types/api';
import { useCurrentUser } from '../../../contexts/UserContext';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalL,
  },
});

interface MarkerFormProps {
  marker?: Marker;
  onSave: (marker: MarkerCreate) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: ApiErrorInfo | null;
}

const MarkerForm: React.FC<MarkerFormProps> = ({
  marker,
  onSave,
  onCancel,
  loading,
  error
}) => {
  const styles = useStyles();
  const { currentUser } = useCurrentUser();

  const [formData, setFormData] = React.useState<MarkerCreate>(
    marker ? {
      usuarioId: marker.usuarioId,
      descricao: marker.descricao,
    } : {
      usuarioId: currentUser?.id || '',
      descricao: '',
    }
  );

  const [fieldErrors, setFieldErrors] = React.useState({
    descricao: '',
  });

  const validateField = (field: keyof typeof fieldErrors, value: string, touched: boolean = false) => {
    let error = '';

    switch (field) {      
      case 'descricao':
        if (!value.trim()) {
          error = 'Descrição é obrigatória';
        }
        break;
    }

    setFieldErrors(prev => ({ ...prev, [field]: touched ? error : '' }));
    return error === '';
  };

  const handleInputChange = (field: keyof MarkerCreate, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validar campo em tempo real apenas se já foi tocado
    if (fieldErrors[field as keyof typeof fieldErrors]) {
      validateField(field as keyof typeof fieldErrors, value, true);
    }
  };

  const handleBlur = (field: keyof typeof fieldErrors) => {
    validateField(field, formData[field as keyof typeof formData] as string, true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Marcar todos os campos como tocados e validar
    setFieldErrors({
      descricao: formData.descricao.trim() ? '' : 'Descrição é obrigatória',
    });

    // Verificar se há erros
    const hasErrors = !formData.descricao.trim();

    if (hasErrors) {
      return;
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {loading && (
        <MessageBar intent="info">
          <MessageBarBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Spinner size="tiny" />
              Estamos salvando o marcador. Isso pode levar alguns segundos...
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent={error.isAuthError ? "warning" : "error"}>
          <MessageBarBody>
            {error.isNotFound && "Marcador não encontrado. Verifique se o ID está correto."}
            {error.isValidationError && error.message}
            {error.isAuthError && "Acesso negado. Você não tem permissão para esta operação."}
            {!error.isNotFound && !error.isValidationError && !error.isAuthError && error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.section}>
        <Field
          label="Descrição"
          required
          validationMessage={fieldErrors.descricao}
          validationState={fieldErrors.descricao ? 'error' : 'none'}
        >
          <Input
            size="large"
            value={formData.descricao}
            onChange={(e) => handleInputChange('descricao', e.target.value)}
            onBlur={() => handleBlur('descricao')}
            placeholder="Descrição do marcador"
          />
        </Field>
      </div>

      <div className={styles.actions}>
        <Button appearance="subtle" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type="submit"
          appearance="primary"
          disabled={loading || Object.values(fieldErrors).some(error => error !== '')}
        >
          {loading ? 'Salvando...' : marker ? 'Atualizar' : 'Criar'} Marcador
        </Button>
      </div>
    </form>
  );
};

export default MarkerForm;